import type { LegacyReduxPlan, LegacyPlan } from 'types/legacyDashboard';
import filterUndefined from 'utils/arrays/filterUndefined';

const zoneHasAllCarrierIds = (zoneCarriers: Array<CarrierId>, carrierIds: Array<CarrierId>) =>
  !carrierIds.some((id) => !zoneCarriers[id]);

/**
 * Convert a legacy API plan to a legacy plan. Dashboard stores LegacyReduxPlan in redux, and selects using the reduced version.
 * @param plansAcc
 * @param plan
 * @param carrierIds
 * @param createDisplayName
 * @returns Converted legacy API plans to legacy plans
 */
const reducePlans = (
  plansAcc: Array<LegacyPlan>,
  plan: LegacyReduxPlan,
  carrierIds: Array<CarrierId> = [],
  createDisplayName = (pl: LegacyReduxPlan) => pl.name
): Array<LegacyPlan> => {
  if (!plan?.zones) {
    return plansAcc;
  }

  const plansByZone: Array<LegacyPlan> | undefined = filterUndefined(
    plan.zones.map((zone) => {
      if (!zone) {
        return undefined;
      }

      const currentTier = plan.tiers[plan.current_tier] ?? {};
      const currentZone = currentTier?.zones[zone] ?? {};
      const planName = plan.name;

      if (carrierIds && carrierIds.length > 0) {
        const zoneCarriers = plan.zoneDetails[zone].carriers;
        if (!zoneCarriers || zoneCarriers.length === 0) {
          // We were asked to filter data, but there was nothing on which to filter.
          // Either we should ignore that "bad data" as eventually this sim would
          // fail to activate (current) or show it anyway and let downstream activation handle it
          return undefined;
        }
        if (!zoneHasAllCarrierIds(zoneCarriers, carrierIds)) {
          return undefined;
        }
      }

      return {
        id: plan.id,
        groupId: plan.groupid,
        planName,
        dataLimit: plan.data,
        displayName: createDisplayName(plan),
        displayCategory: plan.display_category,
        zone,
        perLineCharge: parseFloat(currentZone?.amount),
        overageCharge: parseFloat(currentZone?.overage),
        outboundSms: parseFloat(currentZone?.sms),
        cellularCoverageRegion: plan.cellular_coverage_region,
        tiers: plan.tiers,
        currentTier: plan.current_tier,
        variablerate: plan.variablerate,
        zeroDataRate:
          currentZone?.zero_data_rate_flag === 1 && currentZone?.zero_data_rate
            ? parseFloat(currentZone.zero_data_rate)
            : undefined,
        apns: [
          ...new Set(Object.values(currentZone?.carriers ?? {}).map((carrier) => carrier.apn)),
        ],
      };
    })
  );

  if (!plansByZone) {
    return plansAcc;
  }
  return [...plansAcc, ...plansByZone];
};

export default reducePlans;
